/**
 * Transform Margin Report to Grid Rows
 *
 * Transforms the API response into flat rows for BStockReportGrid.
 * Percentages are calculated dynamically in the grid columns via calculateCellValue.
 * @see US-25d.12 - Composant MarginReportTable
 */

import type {
  MarginReportResponse,
  MarginReportGridRow,
  VarianceType,
} from '@bstock/shared';

/**
 * Determine variance type based on comparison
 * For food cost: higher real = unfavorable
 * For margin: lower real = unfavorable
 */
function getVariance(real: number, theoretical: number, isReversed = false): VarianceType {
  if (real === theoretical) return 'neutral';
  if (isReversed) {
    return real < theoretical ? 'unfavorable' : 'favorable';
  }
  return real > theoretical ? 'unfavorable' : 'favorable';
}

/**
 * Transform API response to grid rows
 * Returns raw values only - percentages are calculated in the grid columns
 */
export function transformMarginReportToGrid(
  report: MarginReportResponse
): MarginReportGridRow[] {
  const rows: MarginReportGridRow[] = [];

  // =============================================================
  // SECTION: CHIFFRE D'AFFAIRES
  // =============================================================
  rows.push({
    id: 'ca-brut',
    section: "CHIFFRE D'AFFAIRES",
    label: 'Ventes Brutes HT',
    rowType: 'item',
    theoValue: report.chiffreAffaires.ventesBrutesCentimes,
    reelValue: report.chiffreAffaires.ventesBrutesCentimes,
  });

  rows.push({
    id: 'ca-net',
    section: "CHIFFRE D'AFFAIRES",
    label: 'Ventes Nettes HT (100%)',
    rowType: 'total',
    theoValue: report.chiffreAffaires.ventesNettesCentimes,
    reelValue: report.chiffreAffaires.ventesNettesCentimes,
  });

  // =============================================================
  // SECTION: STOCK (Real only)
  // =============================================================
  rows.push({
    id: 'stock-initial',
    section: 'STOCK',
    label: 'Stock Initial',
    rowType: 'item',
    theoValue: null,
    reelValue: report.stock.initialTotal,
  });

  // Detail by market type
  if (report.stock.initialByMarketType.food > 0) {
    rows.push({
      id: 'stock-initial-food',
      section: 'STOCK',
      label: '    → FOOD',
      rowType: 'subitem',
      theoValue: null,
      reelValue: report.stock.initialByMarketType.food,
    });
  }
  if (report.stock.initialByMarketType.foodInd > 0) {
    rows.push({
      id: 'stock-initial-food-ind',
      section: 'STOCK',
      label: '    → FOOD IND',
      rowType: 'subitem',
      theoValue: null,
      reelValue: report.stock.initialByMarketType.foodInd,
    });
  }
  if (report.stock.initialByMarketType.nonFood > 0) {
    rows.push({
      id: 'stock-initial-non-food',
      section: 'STOCK',
      label: '    → NON FOOD',
      rowType: 'subitem',
      theoValue: null,
      reelValue: report.stock.initialByMarketType.nonFood,
    });
  }
  if (report.stock.initialByMarketType.nonFoodInd > 0) {
    rows.push({
      id: 'stock-initial-non-food-ind',
      section: 'STOCK',
      label: '    → NON FOOD IND',
      rowType: 'subitem',
      theoValue: null,
      reelValue: report.stock.initialByMarketType.nonFoodInd,
    });
  }

  rows.push({
    id: 'stock-entrees',
    section: 'STOCK',
    label: 'Entrees (Receptions)',
    rowType: 'item',
    theoValue: null,
    reelValue: report.stock.entrees,
  });

  rows.push({
    id: 'stock-sorties',
    section: 'STOCK',
    label: 'Sorties (Transferts)',
    rowType: 'item',
    theoValue: null,
    reelValue: report.stock.sorties,
  });

  rows.push({
    id: 'stock-final',
    section: 'STOCK',
    label: 'Stock Final',
    rowType: 'item',
    theoValue: null,
    reelValue: report.stock.finalTotal,
  });

  // =============================================================
  // SECTION: FOOD COST
  // =============================================================
  rows.push({
    id: 'fc-brut',
    section: 'FOOD COST',
    label: 'Food Cost Brut',
    rowType: 'item',
    theoValue: report.foodCost.brutTheorique,
    reelValue: report.foodCost.brutReel,
    variance: getVariance(report.foodCost.brutReel, report.foodCost.brutTheorique),
  });

  rows.push({
    id: 'marge-brute',
    section: 'FOOD COST',
    label: 'Marge Brute',
    rowType: 'item',
    theoValue: report.foodCost.margeBruteTheorique,
    reelValue: report.foodCost.margeBruteReelle,
    variance: getVariance(report.foodCost.margeBruteReelle, report.foodCost.margeBruteTheorique, true),
  });

  // =============================================================
  // SECTION: DEDUCTIONS
  // =============================================================
  rows.push({
    id: 'deductions-promos',
    section: 'DEDUCTIONS',
    label: 'Promotions / Offerts',
    rowType: 'item',
    theoValue: report.deductions.promos,
    reelValue: report.deductions.promos,
  });

  rows.push({
    id: 'deductions-repas',
    section: 'DEDUCTIONS',
    label: 'Repas Personnel',
    rowType: 'item',
    theoValue: report.deductions.employeeMeals,
    reelValue: report.deductions.employeeMeals,
  });

  rows.push({
    id: 'deductions-pertes',
    section: 'DEDUCTIONS',
    label: 'Pertes connues',
    rowType: 'item',
    theoValue: report.deductions.pertesTotal,
    reelValue: report.deductions.pertesTotal,
  });

  // Detail pertes par type (toujours affiche pour permettre les formules Excel)
  rows.push({
    id: 'pertes-brut',
    section: 'DEDUCTIONS',
    label: '    → Pertes Brut',
    rowType: 'subitem',
    theoValue: report.deductions.pertesByType.raw,
    reelValue: report.deductions.pertesByType.raw,
  });
  rows.push({
    id: 'pertes-fini',
    section: 'DEDUCTIONS',
    label: '    → Pertes Fini',
    rowType: 'subitem',
    theoValue: report.deductions.pertesByType.finished,
    reelValue: report.deductions.pertesByType.finished,
  });
  rows.push({
    id: 'pertes-exceptionnel',
    section: 'DEDUCTIONS',
    label: '    → Pertes Excep.',
    rowType: 'subitem',
    theoValue: report.deductions.pertesByType.exceptional,
    reelValue: report.deductions.pertesByType.exceptional,
  });

  // =============================================================
  // SECTION: FOOD COST NET
  // =============================================================
  rows.push({
    id: 'fc-net',
    section: 'FOOD COST NET',
    label: 'Food Cost Net',
    rowType: 'total',
    theoValue: report.foodCostNet.theorique,
    reelValue: report.foodCostNet.reel,
    variance: getVariance(report.foodCostNet.reel, report.foodCostNet.theorique),
  });

  // =============================================================
  // SECTION: ECARTS
  // =============================================================
  rows.push({
    id: 'ecart-total',
    section: 'ECARTS',
    label: 'Portions Controle (ecart total)',
    rowType: 'item',
    theoValue: null,
    reelValue: report.ecarts.total,
    variance: report.ecarts.total > 0 ? 'unfavorable' : report.ecarts.total < 0 ? 'favorable' : 'neutral',
  });

  rows.push({
    id: 'ecart-bulk',
    section: 'ECARTS',
    label: '    → dont Bulk',
    rowType: 'subitem',
    theoValue: null,
    reelValue: report.ecarts.bulk,
  });

  rows.push({
    id: 'ecart-nonbulk',
    section: 'ECARTS',
    label: '    → dont Non-Bulk (demarque)',
    rowType: 'subitem',
    theoValue: null,
    reelValue: report.ecarts.nonBulk,
    variance: report.ecarts.nonBulk > 0 ? 'unfavorable' : report.ecarts.nonBulk < 0 ? 'favorable' : 'neutral',
  });

  // =============================================================
  // SECTION: HORS FOOD COST
  // =============================================================
  rows.push({
    id: 'hors-fc-maintenance',
    section: 'HORS FOOD COST',
    label: 'Consommation Maintenance',
    rowType: 'item',
    theoValue: null,
    reelValue: report.horsFoodCost.maintenanceCentimes,
  });

  // En fin de rapport : inseree plus haut, elle decalerait les lignes
  // suivantes et casserait les formules baties sur l'export Excel.
  rows.push({
    id: 'fc-brut-bulk',
    section: 'FOOD COST',
    label: 'Food Cost Brut Bulk',
    rowType: 'item',
    theoValue: null,
    reelValue: report.foodCost.brutReelBulk,
  });

  return rows;
}
